import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { fetchCommercialReporting } from "./commercialReportApi";
import {
  COMMERCIAL_REPORT_METRICS,
  commercialPhaseChartData,
  formatCommercialDays,
  formatCommercialMetric,
  summarizeCommercialReport,
  toCommercialPhaseGridRows,
  toCommercialProjectGridRows,
} from "./commercialReportData";
import { formatBudgetCurrency } from "./expansionBudgetReportData";
import { BudgetBarChart, BudgetPieChart } from "./reportingCharts";

function currencyFormatter(params) {
  return formatBudgetCurrency(params.value);
}

function daysFormatter(params) {
  return formatCommercialDays(params.value);
}

const PHASE_SUMMARY_COLUMNS = [
  { field: "phase", headerName: "Phase", width: 130, pinned: "left" },
  { field: "projectCount", headerName: "Projects", width: 110, type: "numericColumn" },
  {
    field: "totalMrc",
    headerName: "Total MRC",
    width: 130,
    valueFormatter: currencyFormatter,
    type: "numericColumn",
  },
  {
    field: "projectsWithMrc",
    headerName: "With MRC",
    width: 110,
    type: "numericColumn",
  },
  {
    field: "avgDaysInPhase",
    headerName: "Avg Days in Phase",
    width: 140,
    valueFormatter: daysFormatter,
    type: "numericColumn",
  },
  { field: "phaseStays", headerName: "Phase Stays", width: 110, type: "numericColumn" },
];

const PROJECT_DETAIL_COLUMNS = [
  { field: "workOrder", headerName: "Work Order", width: 120, pinned: "left" },
  { field: "customer", headerName: "Customer", width: 160 },
  { field: "project", headerName: "Project", width: 180 },
  { field: "phase", headerName: "Phase", width: 120 },
  { field: "subphase", headerName: "Subphase", width: 150 },
  { field: "status", headerName: "Status", width: 100 },
  {
    field: "mrc",
    headerName: "MRC",
    width: 110,
    valueFormatter: currencyFormatter,
    type: "numericColumn",
  },
  {
    field: "daysInCurrentPhase",
    headerName: "Days in Phase",
    width: 120,
    valueFormatter: daysFormatter,
    type: "numericColumn",
  },
];

export function CommercialReportingPanel({ gridTheme }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const [metricKey, setMetricKey] = useState(COMMERCIAL_REPORT_METRICS[0].key);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchCommercialReporting();
      setReport(data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not load commercial reporting.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const byPhase = report?.by_phase ?? [];
  const chartData = useMemo(
    () => commercialPhaseChartData(byPhase, metricKey),
    [byPhase, metricKey]
  );
  const summary = useMemo(
    () => summarizeCommercialReport(byPhase, metricKey),
    [byPhase, metricKey]
  );
  const phaseGridRows = useMemo(() => toCommercialPhaseGridRows(byPhase), [byPhase]);
  const projectGridRows = useMemo(
    () => toCommercialProjectGridRows(report?.projects),
    [report?.projects]
  );

  const metricLabel =
    COMMERCIAL_REPORT_METRICS.find((m) => m.key === metricKey)?.label || metricKey;
  const formatMetric = useCallback(
    (value) => formatCommercialMetric(value, metricKey),
    [metricKey]
  );

  const durationNote =
    report?.phase_duration_source === "project_phase_history"
      ? "Average days per phase uses completed intervals from Project Phase History (admin table)."
      : report?.phase_duration_source === "created_at_fallback"
        ? "Average days per phase uses project creation date until phase history records exist. Complete phases on projects to populate the dedicated history table."
        : "Average days per phase uses change log until enough Project Phase History records exist.";

  return (
    <div className="reporting-panel">
      <div className="reporting-filters">
        <div className="reporting-filter">
          <label htmlFor="commercial-report-metric">Metric</label>
          <select
            id="commercial-report-metric"
            value={metricKey}
            onChange={(e) => setMetricKey(e.target.value)}
          >
            {COMMERCIAL_REPORT_METRICS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? <p className="reporting-status">Loading commercial reporting…</p> : null}
      {error ? <p className="reporting-status reporting-status--error">{error}</p> : null}

      {!loading && !error && report ? (
        <>
          <p className="reporting-note">
            {durationNote}{" "}
            <Link to="/admin/phase-history">View phase history (admin)</Link>
          </p>

          <div className="reporting-kpis">
            <div className="reporting-kpi">
              <div className="reporting-kpi-label">Commercial Projects</div>
              <div className="reporting-kpi-value">{report.commercial_project_count}</div>
            </div>
            <div className="reporting-kpi">
              <div className="reporting-kpi-label">Portfolio MRC</div>
              <div className="reporting-kpi-value">{formatBudgetCurrency(report.total_mrc)}</div>
            </div>
            <div className="reporting-kpi">
              <div className="reporting-kpi-label">With Phase History</div>
              <div className="reporting-kpi-value">{report.projects_with_phase_history}</div>
            </div>
            <div className="reporting-kpi">
              <div className="reporting-kpi-label">Total {metricLabel}</div>
              <div className="reporting-kpi-value">{formatMetric(summary.total)}</div>
            </div>
          </div>

          <div className="reporting-charts">
            <section className="reporting-chart-card">
              <h3 className="reporting-chart-title">{metricLabel} by Phase</h3>
              <BudgetPieChart data={chartData} formatValue={formatMetric} />
            </section>
            <section className="reporting-chart-card">
              <h3 className="reporting-chart-title">{metricLabel} comparison</h3>
              <BudgetBarChart data={chartData} formatValue={formatMetric} />
            </section>
          </div>

          <h3 className="reporting-section-title">Phase totals</h3>
          <div className="reporting-grid-wrap">
            <AgGridReact
              theme={gridTheme}
              rowData={phaseGridRows}
              columnDefs={PHASE_SUMMARY_COLUMNS}
              getRowId={(params) => params.data.id}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 80,
              }}
              domLayout="autoHeight"
              suppressCellFocus
            />
          </div>

          <h3 className="reporting-section-title">Projects</h3>
          <div className="reporting-grid-wrap">
            <AgGridReact
              theme={gridTheme}
              rowData={projectGridRows}
              columnDefs={PROJECT_DETAIL_COLUMNS}
              getRowId={(params) => params.data.id}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 80,
              }}
              domLayout="autoHeight"
              suppressCellFocus
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

export default CommercialReportingPanel;
